import { lazy, Suspense, useEffect, useMemo, useState } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import Onboarding from '@/features/organization/onboarding'
import { useSession } from '@/shared/hooks/useSession'
import { LoadingScreen } from '@/shared/components'
import { AppStateContext } from './hooks/useAppState'
import { useAppConfig } from '@/shared/hooks/api/appConfig'
import {
  areMandatoryInfoToCreateInstallationCompleted,
  useInstallations
} from '@/features/installations/hooks/useInstallations'
import { usePermissions } from '@/shared/hooks/usePermissions'
import ChangePassword from '@/features/auth/pages/change-password'
import Installations from '@/features/installations/pages'
import Importers from '@/features/importers'
import Users from '@/features/organization/users'
import PdfReports from '@/features/reports/pages/pdf-reports'
import NoAccess from '@/features/auth/pages/NoAccess'
import SettingsPage from '@/features/organization/settings'
import DocsPage from '@/features/docs/pages'
import InstallationSetup from '@/features/installations/pages/installation-setup'
import Dashboard from '@/features/dashboard/pages'
import { useDoesOrganizationRequiresLocalAddress } from '@/shared/hooks/api/organizations'
import { Navbar } from '@/shared/components'

const MesIntegration = lazy(() => import('@/features/mes-integration'))
const Suppliers = lazy(() => import('@/features/suppliers'))

export default function App() {
  const { session } = useSession()
  const { isLoaded: isConfigLoaded, getConfig } = useAppConfig()

  const { checkPermission } = usePermissions()
  const [selectedInstallationId, setSelectedInstallationId] = useState<string | null>(null)

  const { organization, passwordResetRequired } = session
  const isMandatoryInfoComplete = organization.isMandatoryInfoComplete

  const doesOrganizationRequiresLocalAddress = useDoesOrganizationRequiresLocalAddress(
    organization.placeOfRegistration
  )
  const isVerified = organization.activatedAt !== null

  const { data } = useInstallations()
  const installations = useMemo(() => {
    return data ?? []
  }, [data])

  const isInstallationSetupComplete =
    areMandatoryInfoToCreateInstallationCompleted(
      organization,
      organization.country,
      doesOrganizationRequiresLocalAddress
    ) && installations.length > 0

  useEffect(() => {
    getConfig()
  }, [getConfig])

  if (!isConfigLoaded) {
    return <LoadingScreen />
  }

  if (passwordResetRequired) {
    return (
      <>
        <Routes>
          <Route path="*" element={<ChangePassword />} />
        </Routes>
      </>
    )
  }

  if (!isVerified || !isMandatoryInfoComplete) {
    return (
      <>
        <Routes>
          <Route path="onboarding">
            <Route index path="*" element={<Onboarding />} />
          </Route>
          <Route path="*" element={<Navigate to="/onboarding" />} />
        </Routes>
      </>
    )
  }

  if (location.pathname.includes('pdf-reports')) {
    return (
      <Routes>
        <Route path="pdf-reports">
          <Route index path="*" element={<PdfReports />} />
        </Route>
      </Routes>
    )
  }

  const canManageOrganization = checkPermission('organization', 'update')
  const canManageUsers = checkPermission('organization', 'manage-users')
  const canSeeImporterRequests = checkPermission('importerRequest', 'read')
  const canSeeMesIntegration = checkPermission('integration', 'read')

  return (
    <AppStateContext.Provider
      value={{
        installations,
        selectedInstallationId,
        setSelectedInstallationId
      }}
    >
      <div className="flex flex-col h-full">
        <Navbar session={session} />
        <div className="sm:ml-64 transition-all duration-300 flex flex-col h-full">
          <Routes>
            <Route index element={<Navigate to="/dashboard" />} />
            <Route path="dashboard">
              <Route index path="*" element={<Dashboard />} />
            </Route>
            {canSeeMesIntegration && (
              <Route path="mes">
                <Route
                  index
                  path="*"
                  element={
                    <Suspense fallback={<LoadingScreen />}>
                      <MesIntegration />
                    </Suspense>
                  }
                />
              </Route>
            )}
            <Route path="installations">
              <Route index path="*" element={<Installations />} />
            </Route>
            {canSeeImporterRequests ? (
              <Route path="importers">
                <Route index path="*" element={<Importers />} />
              </Route>
            ) : (
              <Route path="importers">
                <Route index path="*" element={<NoAccess />} />
              </Route>
            )}
            {canManageUsers ? (
              <Route path="users">
                <Route index path="*" element={<Users />} />
              </Route>
            ) : (
              <Route path="users">
                <Route index path="*" element={<NoAccess />} />
              </Route>
            )}
            {canManageOrganization ? (
              <Route path="settings">
                <Route index path="*" element={<SettingsPage />} />
              </Route>
            ) : (
              <Route path="settings">
                <Route index path="*" element={<NoAccess />} />
              </Route>
            )}
            <Route path="suppliers">
              <Route
                index
                path="*"
                element={
                  <Suspense fallback={<LoadingScreen />}>
                    <Suppliers />
                  </Suspense>
                }
              />
            </Route>
            <Route
              path="installation-setup"
              element={
                isInstallationSetupComplete ? <Navigate to={`/dashboard`} /> : <InstallationSetup />
              }
            />
            <Route path="docs" element={<DocsPage />} />
            <Route
              path="*"
              element={
                <Navigate to={isInstallationSetupComplete ? `/dashboard` : '/installation-setup'} />
              }
            />
          </Routes>
        </div>
      </div>
    </AppStateContext.Provider>
  )
}
